import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { LoadingDots } from '@/components/common/LoadingDots';

export function AuthGuard() {
  const { isAuthenticated, isInitialized, initAuth, user } = useAuthStore();
  const [isChecking, setIsChecking] = useState(!isInitialized);
  const location = useLocation();

  useEffect(() => {
    if (!isInitialized) {
      void initAuth().finally(() => setIsChecking(false));
    }
  }, [isInitialized, initAuth]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-base)]">
        <LoadingDots size="large" />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Block unverified email users — but allow through if already on /verify-email
  if (user?.email_verified === false && location.pathname !== '/verify-email') {
    return <Navigate to="/verify-email" replace />;
  }


  // Redirect users who haven't completed onboarding (no env or env not provisioned).
  // Uses server-side `needs_onboarding` flag which checks both active_environment_id
  // and whether the environment has a tier set — covers email/password, OAuth, and
  // re-registration after account deletion.
  const isOnboardingRoute = location.pathname === '/onboarding' || location.pathname.startsWith('/p/');
  if (user?.email_verified !== false && user?.needs_onboarding && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
}
